
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { AvaliacaoDialog } from "@/components/avaliacoes/AvaliacaoDialog";
import { Calendar, User, Building, Clock, RotateCcw, DollarSign, Anchor, Star, Mail, Copy, AlertTriangle, MoreVertical, Edit, Trash2, CheckCircle, FileText, Undo2, CalendarCheck } from "lucide-react";
import { format, differenceInDays } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Link, useNavigate } from "react-router";
import { createLocalDate } from "@/utils/dateUtils";
import type { ControleProjeto, ControleProjetoAvaliacao } from "@/types/controle-projeto";
import { calcularEstadoProjeto, jaTerminou, podeFinalizar, podeRenovar } from "@/utils/controle-projeto-estado";
import { useToast } from "@/hooks/use-toast";
import { SUPABASE_URL } from "@/integrations/supabase/client";
import { useState } from "react";
import { apiFetch } from "@/lib/api-fetch";
import { formatApiError, toUserError } from "@/lib/api-error";
import { reportApiError } from "@/lib/report-error";
import { getPropostaStatusInfo } from "@/utils/proposta-status";
import { useCriarProposta } from "@/hooks/use-finance-propostas";
import type { PropostaPorProjetoItem } from "@/types/finance";
import { projectArea } from "@/utils/project-area";
import { formatCents, parseMoeda } from "@/utils/money";

interface ControleProjetoCardProps {
  projeto: ControleProjeto;
  /** Sem o handler, a ação some do menu (ex.: card aberto fora do controle de projetos). */
  onRenovar?: (projeto: ControleProjeto) => void;
  onEditar?: (projeto: ControleProjeto) => void;
  onExcluir: (projetoId: string) => void;
  onFinalizar?: (projeto: ControleProjeto) => void;
  onDesfazerFinalizacao?: (projeto: ControleProjeto) => void;
  onConfirmarPlanejado?: (projeto: ControleProjeto) => void;
  /** Ver o mesmo contrato do prop homônimo em VagaCard: resolvida em lote pelo pai. */
  proposta?: PropostaPorProjetoItem | null;
}

type BadgeVariant = "default" | "secondary" | "destructive" | "outline";

interface StatusAndamento {
  status: string;
  variant: BadgeVariant;
  isNearEnd: boolean;
}

function periodoPlanejado(planejado: ControleProjeto): string {
  const inicio = planejado.data_inicio ? format(createLocalDate(planejado.data_inicio), "dd/MM/yy") : "";
  const fim = planejado.data_conclusao ? format(createLocalDate(planejado.data_conclusao), "dd/MM/yy") : "sem término";
  return inicio ? `${inicio} a ${fim}` : `até ${fim}`;
}

export function ControleProjetoCard({ projeto, onRenovar, onEditar, onExcluir, onFinalizar, onDesfazerFinalizacao, onConfirmarPlanejado, proposta }: ControleProjetoCardProps) {
  const dataInicio = projeto.data_inicio ? createLocalDate(projeto.data_inicio) : null;
  const dataConclusao = projeto.data_conclusao ? createLocalDate(projeto.data_conclusao) : null;
  const { estado, diasRestantes } = calcularEstadoProjeto(projeto);
  const isRascunho = estado === "rascunho";
  const isFixo = estado === "fixo";
  const isVencido = estado === "vencido";
  const planejados = projeto.planejados ?? [];
  const ultimoPlanejado = planejados[planejados.length - 1];
  const { toast } = useToast();
  const [selectedEvaluation, setSelectedEvaluation] = useState<{avaliacao: any, data: any} | null>(null); // eslint-disable-line @typescript-eslint/no-explicit-any
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const navigate = useNavigate();
  const criarProposta = useCriarProposta();

  const handleGerarProposta = async () => {
    try {
      const nova = await criarProposta.mutateAsync({
        agencia_id: projeto.agencia_id,
        controle_projeto_ids: [projeto.id],
      });
      navigate(`/financeiro/propostas/${nova.id}`);
    } catch (err) {
      const formatted = formatApiError(err);
      toast({ variant: "destructive", title: formatted.title, description: formatted.detail });
    }
  };

  // Badge derivado da mesma fonte de verdade da tela e do kanban (calcularEstadoProjeto).
  const getStatusAndamento = (): StatusAndamento => {
    switch (estado) {
      case "renovado":
        return { status: "Renovado", variant: "secondary", isNearEnd: false };
      case "finalizado":
        return { status: "Finalizado", variant: "default", isNearEnd: false };
      case "rascunho":
        return { status: "Rascunho", variant: "outline", isNearEnd: false };
      case "planejado":
        return { status: "Planejado", variant: "outline", isNearEnd: false };
      case "fixo":
        return { status: "Fixo", variant: "default", isNearEnd: false };
      case "a_iniciar":
        return { status: "A iniciar", variant: "secondary", isNearEnd: false };
      case "nao_renova": {
        const termino = dataConclusao ? format(dataConclusao, "dd/MM") : "";
        return { status: `Termina em ${termino} · não renova`, variant: "outline", isNearEnd: false };
      }
      case "vencido": {
        const diasVencidos = Math.abs(diasRestantes ?? 0);
        return {
          status: diasVencidos === 1 ? "Vencido há 1 dia" : `Vencido há ${diasVencidos} dias`,
          variant: "destructive",
          isNearEnd: false,
        };
      }
      case "em_andamento": {
        if (diasRestantes === null) return { status: "Sem término", variant: "outline", isNearEnd: false };
        const dias = diasRestantes;
        const isNearEnd = dias <= 10;
        return { status: `${dias} dias`, variant: isNearEnd ? "destructive" : "secondary", isNearEnd };
      }
    }
  };

  const calcularDuracao = () => {
    if (isRascunho) {
      return "—";
    }

    if (isFixo) {
      return "Indefinida (Fixo)";
    }

    if (dataConclusao && dataInicio) {
      const dias = differenceInDays(dataConclusao, dataInicio);
      return `${dias}d`;
    }
    return "Indefinida";
  };

  const gerarLinkAvaliacao = async (evaluatorType: 'freelancer' | 'agency') => {
    try {
      const data = await apiFetch<{ url: string }>(
        `${SUPABASE_URL}jobs/evaluations/generate-link/${projeto.id}`,
        {
          method: "POST",
          body: JSON.stringify({ evaluator_type: evaluatorType }),
        },
      );
      
      // Tentar copiar para clipboard com fallback
      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(data.url);
        } else {
          // Fallback para navegadores mais antigos ou contextos não seguros
          const textArea = document.createElement('textarea');
          textArea.value = data.url;
          textArea.style.position = 'fixed';
          textArea.style.left = '-999999px';
          textArea.style.top = '-999999px';
          document.body.appendChild(textArea);
          textArea.focus();
          textArea.select();
          document.execCommand('copy');
          textArea.remove();
        }
        
        toast({
          title: "Link copiado!",
          description: `Link de avaliação para ${evaluatorType === 'freelancer' ? 'freelancer' : 'agência'} copiado para a área de transferência.`,
        });
      } catch (clipboardError) {
        console.error('Erro ao copiar para clipboard:', clipboardError);
        // Exibir o link para o usuário copiar manualmente
        toast({
          title: "Link gerado!",
          description: (
            <div>
              <p>Não foi possível copiar automaticamente. Link:</p>
              <input 
                type="text" 
                value={data.url} 
                readOnly 
                className="w-full mt-1 p-1 text-xs border rounded"
                onClick={(e) => e.currentTarget.select()}
              />
            </div>
          ),
          duration: 10000, // Mais tempo para permitir cópia manual
        });
      }
    } catch (error) {
      reportApiError(error, { source: "controle_projetos", kind: "generate_link" });
      console.error('Erro ao gerar link:', error);
      toast({
        title: "Erro",
        description: "Não foi possível gerar o link de avaliação. " + toUserError(error).description,
        variant: "destructive",
      });
    }
  };

  const enviarEmailAvaliacao = async (evaluatorType: 'freelancer' | 'agency') => {
    try {
      const toEmail = evaluatorType === 'freelancer'
        ? projeto.freelancer?.email
        : projeto.agencia?.email;

      if (!toEmail) {
        toast({
          title: "Email não encontrado",
          description: `Não foi possível encontrar o email do ${evaluatorType === 'freelancer' ? 'freelancer' : 'da agência'}.`,
          variant: "destructive",
        });
        return;
      }

      await apiFetch(`${SUPABASE_URL}jobs/evaluations/send-email/${projeto.id}`, {
        method: "POST",
        body: JSON.stringify({
          evaluator_type: evaluatorType,
          to_email: toEmail,
        }),
      });

      toast({
        title: "Email enviado!",
        description: `Email de avaliação enviado para ${evaluatorType === 'freelancer' ? 'o freelancer' : 'a agência'}.`,
      });
    } catch (error) {
      reportApiError(error, { source: "controle_projetos", kind: "send_email" });
      console.error('Erro ao enviar email:', error);
      toast({
        title: "Erro",
        description: "Não foi possível enviar o email de avaliação. " + toUserError(error).description,
        variant: "destructive",
      });
    }
  };

  const statusAndamento = getStatusAndamento();
  const numeroRenovacoes = projeto.numero_renovacoes || 0;

  const renderAvaliacaoStatus = (evaluation: ControleProjetoAvaliacao | undefined, tipo: 'freelancer' | 'agency') => {
    if (!evaluation) {
      return <span className="text-muted-foreground text-xs">-</span>;
    }

    // Se o status for "email_sent", mostrar "Pendente"
    if (evaluation.status === 'email_sent') {
      return <span className="text-muted-foreground text-xs">E-mail ou link enviado</span>;
    }

    // Se tiver rating ou media_geral, mostrar com estrela
    const displayRating = evaluation.media_geral ?? evaluation.rating;
    if (displayRating) {
      return (
        <Button
          variant="ghost"
          size="sm"
          className="h-auto p-1 text-xs font-normal"
          onClick={() => {
            const avaliacaoData = {
              id: `eval-${projeto.id}-${tipo}`,
              evaluator_type: tipo === 'freelancer' ? 'freelancer' : 'agency',
              status: evaluation.status,
              rating: evaluation.rating,
              media_geral: evaluation.media_geral,
              rated_at: evaluation.completed_at,
              created_at: projeto.data_inicio,
              freelancer_name: projeto.freelancer?.nome,
              agencia_name: projeto.agencia?.nome,
              project_start_date: projeto.data_inicio,
              project_end_date: projeto.data_conclusao,
              controle_projeto_id: projeto.id
            };
            setSelectedEvaluation({
              avaliacao: avaliacaoData,
              data: {
                rating: evaluation.rating,
                feedback_text: evaluation.feedback_text,
                feedback_data: evaluation.feedback_data,
                status: evaluation.status
              }
            });
            setIsDialogOpen(true);
          }}
        >
          <Star className="h-3 w-3 mr-1 fill-yellow-400 text-yellow-400" />
          {displayRating}
        </Button>
      );
    }

    // Caso padrão (se não tem rating mas tem evaluation)
    return <span className="text-muted-foreground text-xs">E-mail ou link enviado</span>;
  };

  return (
    <Card className={`hover:shadow-md transition-shadow ${statusAndamento.isNearEnd ? 'border-orange-400 bg-orange-50 dark:bg-orange-950/20 dark:border-orange-600' : ''} ${isVencido ? 'border-red-300 dark:border-red-800' : ''}`}>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">#{projeto.id.slice(0, 8)}</span>
            {projeto.modalidade === "fixa" && !isFixo && (
              <Badge variant="outline" className="text-xs" title="Contratação para vaga fixa">
                <Anchor className="h-3 w-3 mr-1" />
                Vaga fixa
              </Badge>
            )}
            {numeroRenovacoes > 0 && !isFixo && (
              <Badge variant="outline" className="text-xs bg-blue-50 text-blue-600 border-blue-200 dark:bg-blue-950/50 dark:text-blue-300 dark:border-blue-800">
                {numeroRenovacoes}ª
              </Badge>
            )}
            {ultimoPlanejado && (
              <Badge
                variant="outline"
                className="text-xs"
                title="Renovações já combinadas, confirmadas quando o período anterior termina"
              >
                +{planejados.length} {planejados.length === 1 ? "planejado" : "planejados"}
                {ultimoPlanejado.data_conclusao && ` até ${format(createLocalDate(ultimoPlanejado.data_conclusao), "dd/MM/yy")}`}
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Badge variant={statusAndamento.variant} className={`text-xs ${statusAndamento.isNearEnd ? 'bg-orange-100 text-orange-800 border-orange-300 dark:bg-orange-900/50 dark:text-orange-200 dark:border-orange-600' : ''}`}>
              {isFixo && (
                <Anchor className="h-3 w-3 mr-1" />
              )}
              {estado === "renovado" && (
                <RotateCcw className="h-3 w-3 mr-1" />
              )}
              {(statusAndamento.isNearEnd || isVencido) && (
                <AlertTriangle className="h-3 w-3 mr-1" />
              )}
              {statusAndamento.status}
            </Badge>
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                  <MoreVertical className="h-3 w-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                {podeRenovar(estado) && planejados.length > 0 && onConfirmarPlanejado && (
                  <DropdownMenuItem onClick={() => onConfirmarPlanejado(projeto)}>
                    <CalendarCheck className="h-3 w-3 mr-2" />
                    <span className="flex flex-col">
                      <span>Renovar com o período planejado</span>
                      <span className="text-xs text-muted-foreground">{periodoPlanejado(planejados[0])}</span>
                    </span>
                  </DropdownMenuItem>
                )}
                {podeRenovar(estado) && planejados.length === 0 && onRenovar && (
                  <DropdownMenuItem onClick={() => onRenovar(projeto)}>
                    <RotateCcw className="h-3 w-3 mr-2" />
                    Renovar
                  </DropdownMenuItem>
                )}
                {podeFinalizar(estado) && onFinalizar && (
                  <DropdownMenuItem onClick={() => onFinalizar(projeto)}>
                    <CheckCircle className="h-3 w-3 mr-2" />
                    Finalizar
                  </DropdownMenuItem>
                )}
                {estado === "nao_renova" && onDesfazerFinalizacao && (
                  <DropdownMenuItem onClick={() => onDesfazerFinalizacao(projeto)}>
                    <Undo2 className="h-3 w-3 mr-2" />
                    Desfazer finalização
                  </DropdownMenuItem>
                )}
                {onEditar && (
                  <DropdownMenuItem onClick={() => onEditar(projeto)}>
                    <Edit className="h-3 w-3 mr-2" />
                    Editar
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem 
                  onClick={() => onExcluir(projeto.id)}
                  className="text-red-600 dark:text-red-400"
                >
                  <Trash2 className="h-3 w-3 mr-2" />
                  Excluir
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </CardTitle>
      </CardHeader>
      
      <CardContent className="space-y-2 text-sm">
        <div className="flex items-center gap-2">
          <Building className="h-3 w-3 text-muted-foreground" />
          <span className="font-medium truncate">{projeto.agencia?.nome || 'Agência não encontrada'}</span>
        </div>
        
        <div className="flex items-center gap-2">
          <User className="h-3 w-3 text-muted-foreground" />
          <span className="truncate">{projeto.freelancer?.nome || 'Freelancer não encontrado'}</span>
        </div>
        
        <div className="flex items-center gap-2">
          <span className="font-medium">Área:</span>
          <span className="truncate" title={projeto.area_atuacao}>{projectArea(projeto)}</span>
        </div>

        {projeto.valor_projeto && (
          <div className="flex items-center gap-2">
            <DollarSign className="h-3 w-3 text-muted-foreground" />
            <span className="font-medium text-green-600">
              {formatCents(Math.round(projeto.valor_projeto * 100), parseMoeda(projeto.moeda))}
            </span>
          </div>
        )}

        {proposta !== undefined && (
          <div className="flex items-center gap-2 text-xs">
            {proposta ? (
              <Link
                to={`/financeiro/propostas/${proposta.id}`}
                className="inline-flex items-center gap-1 font-medium text-primary hover:underline"
              >
                <FileText className="h-3 w-3" />
                Proposta {proposta.codigo ?? "Sem código"} · {getPropostaStatusInfo(proposta.status).label}
              </Link>
            ) : (
              <button
                type="button"
                disabled={criarProposta.isPending}
                onClick={handleGerarProposta}
                className="inline-flex items-center gap-1 font-medium text-muted-foreground hover:text-foreground"
              >
                <FileText className="h-3 w-3" />
                Gerar proposta
              </button>
            )}
          </div>
        )}
        
        <div className="grid grid-cols-2 gap-2">
          <div>
            <span className="text-muted-foreground text-xs">Início:</span>
            <div className="flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              <span className="text-sm">{dataInicio ? format(dataInicio, "dd/MM/yy", { locale: ptBR }) : "-"}</span>
            </div>
          </div>
          
          <div>
            <span className="text-muted-foreground text-xs">Fim:</span>
            <div className="flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              <span className="text-sm">{dataConclusao ? format(dataConclusao, "dd/MM/yy", { locale: ptBR }) : "Indefinida"}</span>
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <Clock className="h-3 w-3 text-muted-foreground" />
          <span>Duração: {calcularDuracao()}</span>
        </div>

        {/* Avaliações só para quem já terminou (finalizado ou renovado) */}
        {jaTerminou(estado) && (
          <div className="border-t pt-2 mt-2 space-y-2">
            <span className="text-xs font-medium text-muted-foreground">Avaliações:</span>
            
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div className="space-y-1">
                <span className="text-muted-foreground">Freelancer: </span>
                {renderAvaliacaoStatus(projeto.freelancer_evaluation, 'freelancer')}
                
                {/* Botões de ação para freelancer */}
                <div className="flex gap-1 mt-1">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => gerarLinkAvaliacao('freelancer')}
                    className="h-6 px-2 text-xs"
                    title="Copiar link de avaliação"
                  >
                    <Copy className="h-3 w-3" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => enviarEmailAvaliacao('freelancer')}
                    className="h-6 px-2 text-xs"
                    title="Enviar email de avaliação"
                  >
                    <Mail className="h-3 w-3" />
                  </Button>
                </div>
              </div>

              <div className="space-y-1">
                <span className="text-muted-foreground">Agência: </span>
                {renderAvaliacaoStatus(projeto.agency_evaluation, 'agency')}
                
                {/* Botões de ação para agência */}
                <div className="flex gap-1 mt-1">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => gerarLinkAvaliacao('agency')}
                    className="h-6 px-2 text-xs"
                    title="Copiar link de avaliação"
                  >
                    <Copy className="h-3 w-3" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => enviarEmailAvaliacao('agency')}
                    className="h-6 px-2 text-xs"
                    title="Enviar email de avaliação"
                  >
                    <Mail className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </CardContent>

      {isVencido && onFinalizar && onRenovar && (
        <CardFooter className="gap-2 pt-0 pb-3">
          <Button size="sm" variant="outline" className="flex-1 h-7 text-xs" onClick={() => onFinalizar(projeto)}>
            <CheckCircle className="h-3 w-3 mr-1" />
            Finalizar
          </Button>
          <Button size="sm" className="flex-1 h-7 text-xs" onClick={() => onRenovar(projeto)}>
            <RotateCcw className="h-3 w-3 mr-1" />
            Renovar
          </Button>
        </CardFooter>
      )}

      {selectedEvaluation && (
        <AvaliacaoDialog
          isOpen={isDialogOpen}
          onClose={() => {
            setIsDialogOpen(false);
            setSelectedEvaluation(null);
          }}
          avaliacao={selectedEvaluation.avaliacao}
          evaluationData={selectedEvaluation.data}
        />
      )}
    </Card>
  );
}
